import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Check, Loader2, AlertCircle, Zap, CreditCard, ExternalLink, ArrowLeftRight } from 'lucide-react';
import { api } from '../services/api';
import toast from '../utils/toast';
import { useCredits } from '../contexts/CreditsContext';
import CreditHistory from '../components/CreditHistory';
import usePlans, { normalizePlanName } from '../hooks/usePlans';
import TopUpPacks from '../components/TopUpPacks';
import PlanCards, { BillingToggle, PLAN_FEATURES } from '../components/PlanCards';
import CancelSubscriptionDialog from '../components/CancelSubscriptionDialog';

// ThriveCart customer hub (payments, receipts, card details). The backend has no portal endpoint,
// so the address comes from the frontend build env; the fallback matches the Settings page link.
const THRIVECART_CUSTOMER_URL = process.env.REACT_APP_THRIVECART_CUSTOMER_URL || 'https://thrivecart.com';

const LOW_CREDITS = 100;

const CARD = 'bg-white rounded-xl border border-[#e9e8f1] shadow-[0_1px_2px_rgba(18,15,40,0.05)] p-6';
const MICRO = 'text-[11px] font-bold uppercase tracking-[0.08em] text-[#9794aa] m-0';
const H2 = 'text-base font-bold text-[#16141f] m-0';
const MUTED = 'text-sm text-[#5f5c72] m-0';

const STATUS_STYLES = {
  active: { label: 'Active', className: 'bg-[#e7f6ec] text-[#15803d]' },
  past_due: { label: 'Past due', className: 'bg-[#fdecec] text-[#dc2626]' },
  canceled: { label: 'Cancelled', className: 'bg-gray-100 text-[#5f5c72]' },
  cancelled: { label: 'Cancelled', className: 'bg-gray-100 text-[#5f5c72]' },
};

const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
};

const money = (n) => `$${Number(n).toLocaleString()}`;

// Turns a failed checkout request into a message for the red notice. The backend answers a
// missing ThriveCart product with { error: '... not configured', message: 'Please contact support' }.
const checkoutErrorFrom = (error) => {
  const data = error?.response?.data || {};
  const serverText = typeof data.error === 'string' ? data.error : '';
  const notConfigured = /not configured/i.test(serverText) || /contact support/i.test(data.message || '');
  if (notConfigured) {
    return {
      text: `${serverText || 'This product is not configured'}.`,
      hint: 'This purchase is not set up yet. Please contact support so we can fix it.',
    };
  }
  if (serverText) return { text: serverText, hint: '' };
  if (!error?.response) {
    return { text: 'Could not reach the server.', hint: 'Check your connection and try again.' };
  }
  return { text: 'Failed to start checkout.', hint: 'Please try again in a moment.' };
};

const ErrorNotice = ({ error, onDismiss }) => (
  <div role="alert" className="mt-4 p-3 bg-[#fdecec] border border-red-200 rounded-lg flex items-start gap-2">
    <AlertCircle className="w-4 h-4 text-[#dc2626] flex-shrink-0 mt-0.5" />
    <div className="flex-1 text-sm text-[#991b1b]">
      <p className="m-0 font-semibold">{error.text}</p>
      {error.hint && <p className="m-0 mt-0.5">{error.hint}</p>}
    </div>
    <button
      type="button"
      onClick={onDismiss}
      aria-label="Dismiss"
      className="bg-transparent border-0 cursor-pointer text-[#991b1b] text-lg leading-none px-1"
    >
      ×
    </button>
  </div>
);

const BillingPage = () => {
  const [subscription, setSubscription] = useState(null);
  const { plans, topups, loading: plansLoading, error: plansError, reload: reloadPlans } = usePlans();
  const [billing, setBilling] = useState('monthly');
  const { credits, refreshCredits } = useCredits();
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [topupError, setTopupError] = useState(null);
  const [planError, setPlanError] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const pollRef = useRef(null);
  const cycleSynced = useRef(false);

  const justPaid = searchParams.get('subscribed') === '1';

  useEffect(() => {
    fetchBillingData();
  }, []);

  // Links such as /billing#credits land on the matching section once the page has rendered
  useEffect(() => {
    if (loading || !location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) el.scrollIntoView({ block: 'start' });
  }, [loading, location.hash]);

  // When returning from ThriveCart after payment, auto-poll until subscription activates
  useEffect(() => {
    if (!justPaid) return;
    // Clear the query param from the URL cleanly
    setSearchParams({}, { replace: true });
    toast('Payment received! Activating your account...', { icon: '⏳', duration: 8000 });

    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const subRes = await api.get('/billing/subscription');
        if (subRes.data.has_subscription) {
          clearInterval(pollRef.current);
          toast.success('Subscription activated! Welcome to Outreach Engine.');
          await fetchBillingData();
          navigate('/dashboard', { replace: true });
        }
      } catch (err) {
        console.error('Subscription activation poll error:', err);
      }
      if (attempts >= 10) {
        clearInterval(pollRef.current);
        toast('Taking longer than expected — try refreshing the page.', { icon: 'ℹ️', duration: 6000 });
      }
    }, 3000);

    return () => clearInterval(pollRef.current);
  }, [justPaid]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      refreshCredits();
      try {
        const subRes = await api.get('/billing/subscription');
        if (subRes.data.has_subscription) {
          setSubscription(subRes.data);
          const cycle = subRes.data.subscription?.billing_cycle;
          if (!cycleSynced.current && (cycle === 'monthly' || cycle === 'yearly')) {
            cycleSynced.current = true;
            setBilling(cycle);
          }
        }
      } catch (err) {
        if (err.response?.status !== 404) throw err;
      }
    } catch (error) {
      console.error('Error fetching billing data:', error);
      toast.error('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const handleTopUp = async (pkg) => {
    setTopupError(null);
    try {
      setCheckoutLoading(`topup-${pkg}`);
      const response = await api.post('/billing/tokens/topup', { package: pkg });
      const url = response.data?.checkout_url;
      if (!url) {
        setTopupError({ text: 'Checkout link missing from the server response.', hint: 'Please try again or contact support.' });
        setCheckoutLoading(null);
        return;
      }
      window.location.href = url;
    } catch (error) {
      console.error('Error starting top-up checkout:', error);
      setTopupError(checkoutErrorFrom(error));
      setCheckoutLoading(null);
    }
  };

  const handleSubscribe = async (plan, billingCycle) => {
    setPlanError(null);
    if (!plan.id) {
      toast.error('This plan is not available yet - please contact support.');
      return;
    }
    try {
      setCheckoutLoading(`${plan.name}-${billingCycle}`);
      const response = await api.post('/billing/checkout', {
        plan_id: plan.id,
        billing_cycle: billingCycle,
      });
      const url = response.data?.checkout_url;
      if (!url) {
        setPlanError({ text: 'Checkout link missing from the server response.', hint: 'Please try again or contact support.' });
        setCheckoutLoading(null);
        return;
      }
      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setPlanError(checkoutErrorFrom(error));
      setCheckoutLoading(null);
    }
  };

  const handleCancelSubscription = async (reason) => {
    try {
      const response = await api.post('/billing/cancel', { reason: reason || '', immediate: false });
      return response.data?.success ? response.data : null;
    } catch (error) {
      console.error('Error canceling subscription:', error);
      toast.error(error.response?.data?.error || 'Failed to cancel subscription');
      return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const isSubscribed = !!subscription;
  const sub = subscription?.subscription || {};
  const rawPlanName = String(subscription?.plan?.name || '');
  const catalogPlan = plans.find(p => p.name === normalizePlanName(rawPlanName));
  const planLabel =
    catalogPlan?.display_name ||
    subscription?.plan?.display_name ||
    (rawPlanName ? rawPlanName.charAt(0).toUpperCase() + rawPlanName.slice(1) : '');
  const cycle = sub.billing_cycle === 'yearly' ? 'yearly' : 'monthly';
  const price = catalogPlan ? (cycle === 'yearly' ? catalogPlan.price_yearly : catalogPlan.price_monthly) : null;
  const periodEnd = formatDate(sub.current_period_end);
  const cancelScheduled = !!sub.cancel_at_period_end;
  const status = STATUS_STYLES[String(sub.status || '').toLowerCase()] || {
    label: sub.status || 'Unknown',
    className: 'bg-gray-100 text-[#5f5c72]',
  };
  // Plan cards are offered when there is no plan, or once the current plan is set to cancel
  // (the documented way to change plan). Otherwise a second subscription would be bought.
  const showPlanCards = !isSubscribed || cancelScheduled;

  const allowance = catalogPlan?.monthly_credits || 0;
  const monthlyLeft = credits ? credits.monthly : null;
  const monthlyPct = allowance && monthlyLeft != null
    ? Math.max(0, Math.min(100, Math.round((monthlyLeft / allowance) * 100)))
    : null;

  return (
    <div className="min-h-screen bg-[#f7f7fb] py-12 px-4 sm:px-6 lg:px-8">
      <CancelSubscriptionDialog
        isOpen={cancelOpen}
        onClose={(done) => {
          setCancelOpen(false);
          if (done) fetchBillingData();
        }}
        onConfirm={handleCancelSubscription}
        periodEnd={periodEnd}
        thrivecartUrl={THRIVECART_CUSTOMER_URL}
      />
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="mb-8 max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold text-[#16141f] m-0">Billing</h1>
          <p className="text-[#5f5c72] mt-1 mb-0">Your plan, credits and payments.</p>
        </div>

        {/* Plan + credits summary */}
        <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Your plan */}
          <section className={CARD} aria-labelledby="billing-plan-heading">
            <p className={MICRO}>Your plan</p>
            {isSubscribed ? (
              <>
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  <h2 id="billing-plan-heading" className="text-xl font-bold text-[#16141f] m-0">{planLabel || 'Outreach Engine'}</h2>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${cancelScheduled ? 'bg-[#fdf1df] text-[#b45309]' : status.className}`}>
                    {cancelScheduled ? 'Cancelling' : status.label}
                  </span>
                </div>
                {price != null && (
                  <p className="text-2xl font-bold text-[#16141f] mt-3 mb-0 tabular-nums">
                    {money(price)}
                    <span className="text-sm font-medium text-[#5f5c72]"> / {cycle === 'yearly' ? 'year' : 'month'}</span>
                  </p>
                )}
                <dl className="mt-4 mb-0 space-y-2 text-sm">
                  <div className="flex justify-between gap-4">
                    <dt className="text-[#5f5c72]">Billing</dt>
                    <dd className="m-0 font-medium text-[#16141f]">{cycle === 'yearly' ? 'Yearly' : 'Monthly'}</dd>
                  </div>
                  {periodEnd && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-[#5f5c72]">{cancelScheduled ? 'Cancels on' : 'Renews on'}</dt>
                      <dd className="m-0 font-medium text-[#16141f] tabular-nums">{periodEnd}</dd>
                    </div>
                  )}
                  {allowance > 0 && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-[#5f5c72]">Credits included</dt>
                      <dd className="m-0 font-medium text-[#16141f] tabular-nums">{allowance.toLocaleString()} / month</dd>
                    </div>
                  )}
                </dl>
                {cancelScheduled && (
                  <div className="mt-4 p-3 bg-[#fdf1df] border border-amber-200 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-[#b45309] flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-[#92400e] m-0">
                      Cancellation scheduled — access until {periodEnd || 'the end of your billing period'}.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                <h2 id="billing-plan-heading" className="text-xl font-bold text-[#16141f] mt-2 mb-0">No active plan</h2>
                <p className={`${MUTED} mt-2`}>Choose a plan below to start searching, verifying and sending.</p>
              </>
            )}
          </section>

          {/* Credits */}
          <section className={CARD} aria-labelledby="billing-credits-heading">
            <div className="flex items-center justify-between gap-3">
              <p className={MICRO} id="billing-credits-heading">Credits</p>
              <Zap className="w-4 h-4 text-indigo-600" />
            </div>
            <p className="text-3xl font-bold text-[#16141f] mt-2 mb-0 tabular-nums">
              {credits ? credits.total.toLocaleString() : '—'}
            </p>
            <p className="text-xs text-[#9794aa] m-0">total available</p>

            {credits && (
              <>
                <div className="mt-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#5f5c72]">Monthly credits left</span>
                    <span className="font-medium text-[#16141f] tabular-nums">
                      {credits.monthly.toLocaleString()}{allowance > 0 ? ` of ${allowance.toLocaleString()}` : ''}
                    </span>
                  </div>
                  {monthlyPct != null && (
                    <progress
                      value={monthlyPct}
                      max={100}
                      aria-label="Monthly credits left"
                      className="block mt-1.5 h-1.5 w-full appearance-none border-0 rounded-full overflow-hidden bg-[#f1ebfe] [&::-webkit-progress-bar]:bg-[#f1ebfe] [&::-webkit-progress-bar]:rounded-full [&::-webkit-progress-value]:bg-indigo-600 [&::-webkit-progress-value]:rounded-full [&::-moz-progress-bar]:bg-indigo-600 [&::-moz-progress-bar]:rounded-full"
                    />
                  )}
                  {allowance > 0 && (
                    <p className="text-xs text-[#9794aa] mt-1 mb-0">
                      {Math.max(0, allowance - credits.monthly).toLocaleString()} used this period · resets when your plan renews
                    </p>
                  )}
                </div>
                <div className="mt-3 flex justify-between text-sm">
                  <span className="text-[#5f5c72]">Top-up credits <span className="text-[#9794aa]">(never expire)</span></span>
                  <span className="font-medium text-[#16141f] tabular-nums">{credits.topup.toLocaleString()}</span>
                </div>
              </>
            )}

            {credits && credits.total < LOW_CREDITS && (
              <div className="mt-4 p-3 bg-[#fdf1df] border border-amber-200 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-[#b45309] flex-shrink-0" />
                <p className="text-sm text-[#92400e] m-0">Running low — top up to keep searching and verifying prospects.</p>
              </div>
            )}

            <a href="#credits" className="inline-block mt-4 text-sm font-semibold text-indigo-600 hover:text-indigo-700 no-underline">
              Buy more credits ↓
            </a>
          </section>
        </div>

        {/* Payments and invoices */}
        <section className={`${CARD} max-w-3xl mx-auto mb-6`} aria-labelledby="billing-payments-heading">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-[#f1ebfe] rounded-lg">
                <CreditCard className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h2 id="billing-payments-heading" className={H2}>Payments and invoices</h2>
                <p className={`${MUTED} mt-1`}>Payments, receipts and card details are handled securely by ThriveCart.</p>
              </div>
            </div>
            <a
              href={THRIVECART_CUSTOMER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-[10px] no-underline transition-colors"
            >
              Manage payments and invoices on ThriveCart
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </section>

        {/* Buy more credits */}
        <section id="credits" className={`${CARD} max-w-3xl mx-auto mb-6 scroll-mt-6`} aria-labelledby="billing-topup-heading">
          <h2 id="billing-topup-heading" className={H2}>Buy more credits</h2>
          <p className={`${MUTED} mt-1 mb-4`}>One-off packs. Top-up credits never expire and are used after your monthly credits.</p>
          <TopUpPacks
            topups={topups}
            loading={plansLoading}
            error={plansError}
            onReload={reloadPlans}
            onSelect={handleTopUp}
            busyKey={checkoutLoading}
          />
          {topupError && <ErrorNotice error={topupError} onDismiss={() => setTopupError(null)} />}

          <h3 className="text-sm font-semibold text-[#16141f] mt-6 mb-3">Credit history</h3>
          <CreditHistory />
        </section>

        {/* Plans */}
        <section id="plans" className="mb-8 scroll-mt-6" aria-labelledby="billing-plans-heading">
          {isSubscribed && !cancelScheduled && (
            <div className={`${CARD} max-w-3xl mx-auto`}>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-[#f1ebfe] rounded-lg">
                  <ArrowLeftRight className="w-5 h-5 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <h2 id="billing-plans-heading" className={H2}>Want a different plan?</h2>
                  <p className={`${MUTED} mt-1`}>
                    Cancel your current plan (it stays active until the end of your billing period), then subscribe to the new plan.
                    Your top-up credits are kept.
                  </p>
                  <ul className="text-sm text-[#5f5c72] mt-3 mb-0 pl-5 space-y-1">
                    <li>Billing is stopped for you automatically; if that fails you will be shown how to finish in ThriveCart.</li>
                    <li>Once your plan is set to cancel, the plans appear here so you can choose the new one.</li>
                    <li>When the new plan starts, your monthly credits are set to that plan's allowance.</li>
                  </ul>
                  <button
                    type="button"
                    onClick={() => setCancelOpen(true)}
                    disabled={cancelScheduled}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 hover:border-red-300 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {cancelScheduled ? 'Cancellation scheduled' : 'Cancel subscription'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {showPlanCards && (
            <>
              <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div>
                  <h2 id="billing-plans-heading" className="text-lg font-bold text-[#16141f] m-0">
                    {isSubscribed ? 'Choose your new plan' : 'Subscribe'}
                  </h2>
                  {isSubscribed && (
                    <p className={`${MUTED} mt-1`}>
                      Your current plan stays active until {periodEnd || 'the end of your billing period'}. Your top-up credits are kept.
                    </p>
                  )}
                </div>
                <BillingToggle billing={billing} onChange={setBilling} />
              </div>
              {planError && <div className="mb-4"><ErrorNotice error={planError} onDismiss={() => setPlanError(null)} /></div>}
              {plansLoading ? (
                <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
              ) : plansError ? (
                <div className={`${CARD} text-center`}>
                  <p className={`${MUTED} mb-3`}>We couldn't load the plans right now.</p>
                  <button onClick={reloadPlans} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 border-0 cursor-pointer">Try again</button>
                </div>
              ) : (
                <PlanCards
                  plans={plans}
                  gridClassName="grid grid-cols-1 xl:grid-cols-3 gap-6 items-stretch"
                  billing={billing}
                  onSelect={handleSubscribe}
                  loadingKey={checkoutLoading}
                  currentKey={isSubscribed ? `${normalizePlanName(subscription.plan?.name)}-${sub.billing_cycle}` : null}
                  buttonLabel={(plan) => (isSubscribed ? `Switch to ${plan.display_name}` : `Subscribe to ${plan.display_name}`)}
                />
              )}
            </>
          )}
        </section>

        {/* What's included (subscribed users) */}
        {isSubscribed && (
          <section className={`${CARD} mb-6 max-w-3xl mx-auto`}>
            <h2 className={`${H2} mb-4`}>What's included</h2>
            <ul className="grid sm:grid-cols-2 gap-y-2.5 gap-x-6 m-0 p-0 list-none">
              {PLAN_FEATURES.map(f => (
                <li key={f} className="flex items-center gap-2.5 text-sm text-[#5f5c72]">
                  <Check className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* FAQ */}
        <section className={`${CARD} max-w-3xl mx-auto`}>
          <h2 className={`${H2} mb-4`}>Frequently asked questions</h2>
          <div className="space-y-5">
            <div>
              <p className="text-sm font-semibold text-[#16141f] mt-0 mb-1">What are credits?</p>
              <p className="text-sm text-[#5f5c72] m-0">Credits pay for finding prospects and verifying their email addresses before you send, which protects your sender reputation. Your plan includes a monthly credit allowance. Top-up credits can be bought any time and never expire.</p>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#16141f] mt-0 mb-1">What payment methods are accepted?</p>
              <p className="text-sm text-[#5f5c72] m-0">All major credit and debit cards plus PayPal — processed securely through ThriveCart.</p>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#16141f] mt-0 mb-1">Can I cancel anytime?</p>
              <p className="text-sm text-[#5f5c72] m-0">Yes — cancel from this page at any time. You retain access until the end of your billing period. No lock-in, no cancellation fees.</p>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#16141f] mt-0 mb-1">Is annual billing available?</p>
              <p className="text-sm text-[#5f5c72] m-0">Yes - pay yearly and get 2 months free. You still receive your plan's credits every month.</p>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default BillingPage;
